import React from "react";
import { CopyOutlined } from "@ant-design/icons";
import { Button, Dropdown, Space, Tooltip, message } from "antd";
import dayjs from "dayjs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  SALES_SMS_TEMPLATE_TYPE_LABEL,
  type SalesMeeting,
  type SalesMeetingSmsReminderKind,
  type SalesSmsTemplate,
} from "@fitness-app/data-models";

import { useUserRole } from "~/hooks/trainer/useUserRole";
import { supabase } from "~/store/initializeStore";

import { copyTextToClipboard } from "./SalesAgent/messageUtils";
import { type SalesMeetingCapabilities } from "./salesCrmAccess";

const MEETINGS_QUERY_KEY = ["salesCrmMeetings"];
const ENABLED_TEMPLATES_QUERY_KEY = ["salesSmsTemplates", "enabled"];

const SMS_MARKER_ORDER: SalesMeetingSmsReminderKind[] = ["dayBefore", "morning", "hourBefore", "noAnswer"];

const SMS_MARKER_LABEL: Record<SalesMeetingSmsReminderKind, string> = {
  dayBefore: "Dzień przed",
  morning: "Rano",
  hourBefore: "Godzinę przed",
  noAnswer: "Nie odebrał",
};

const SMS_MARKER_COLOR: Record<SalesMeetingSmsReminderKind, string> = {
  dayBefore: "#30a46c", // green
  morning: "#0090ff", // blue
  hourBefore: "#f5a623", // orange
  noAnswer: "#e5484d", // red
};

const PL_WEEKDAY_PREFIXED = [
  "w niedzielę",
  "w poniedziałek",
  "we wtorek",
  "w środę",
  "w czwartek",
  "w piątek",
  "w sobotę",
];

const getRelativeDayLabel = (start: dayjs.Dayjs): string => {
  const today = dayjs().startOf("day");
  const startDay = start.startOf("day");
  if (startDay.isSame(today, "day")) return "dzisiaj";
  if (startDay.isSame(today.add(1, "day"), "day")) return "jutro";
  return PL_WEEKDAY_PREFIXED[start.day()]!;
};

const splitFullName = (name: string | null | undefined): { firstName: string; lastName: string } => {
  const parts = name?.trim().split(/\s+/).filter(Boolean) ?? [];
  const [firstName = "", ...lastNameParts] = parts;
  return {
    firstName,
    lastName: lastNameParts.join(" "),
  };
};

/** Interpolate a template body with the meeting's variables (mirror of the mobile renderer). */
export const renderSmsTemplateBody = (meeting: SalesMeeting, body: string): string => {
  const start = dayjs(meeting.startTime);
  const seller = splitFullName(meeting.sellerName);
  const variables: Record<string, string> = {
    imie: meeting.clientName?.trim().split(/\s+/)[0] ?? "",
    godzina: start.format("HH:mm"),
    data: start.format("DD.MM.YYYY"),
    dzien: getRelativeDayLabel(start),
    nazwa_spotkania: meeting.calendlyPayload?.scheduled_event?.name ?? "",
    imie_sprzedawcy: seller.firstName,
    nazwisko_sprzedawcy: seller.lastName,
  };
  return body.replace(/\{(\w+)\}/g, (match, key: string) => variables[key] ?? match);
};

const useToggleSmsReminder = () => {
  const queryClient = useQueryClient();
  const { userId } = useUserRole();

  return useMutation({
    mutationFn: async ({
      meeting,
      kind,
      active,
    }: {
      meeting: SalesMeeting;
      kind: SalesMeetingSmsReminderKind;
      active: boolean;
    }) => {
      const timestamp = new Date().toISOString();
      const mark =
        kind === "noAnswer" ? { markedAt: timestamp, by: userId ?? "" } : { sentAt: timestamp, by: userId ?? "" };
      const next = { ...meeting.smsReminders, [kind]: active ? mark : null };
      const { error } = await supabase
        .from("sales_meeting")
        .update({ smsReminders: next } as never)
        .eq("id", meeting.id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MEETINGS_QUERY_KEY }),
    onError: (error) => void message.error(error instanceof Error ? error.message : "Nie udało się zapisać statusu"),
  });
};

/** Clickable row of the four SMS status markers (ptaszki) for a meeting. */
export const SalesSmsStatusMarkers = ({
  capabilities,
  meeting,
}: {
  capabilities: SalesMeetingCapabilities;
  meeting: SalesMeeting;
}): React.ReactElement => {
  const toggle = useToggleSmsReminder();

  return (
    <Space size={6}>
      {SMS_MARKER_ORDER.map((kind) => {
        const active = Boolean(meeting.smsReminders?.[kind]);
        return (
          <Tooltip key={kind} title={SMS_MARKER_LABEL[kind]}>
            <span
              role={capabilities.canUpdate ? "button" : undefined}
              aria-disabled={!capabilities.canUpdate}
              onClick={capabilities.canUpdate ? () => toggle.mutate({ meeting, kind, active: !active }) : undefined}
              style={{
                display: "inline-block",
                width: 14,
                height: 14,
                borderRadius: "50%",
                cursor: capabilities.canUpdate ? "pointer" : "default",
                backgroundColor: active ? SMS_MARKER_COLOR[kind] : "transparent",
                border: active ? "none" : "1.5px solid #d9d9d9",
              }}
            />
          </Tooltip>
        );
      })}
    </Space>
  );
};

/** Copy-only SMS action: pick an enabled template, copy the rendered text, and light its marker. */
export const CopySmsMenu = ({
  capabilities,
  meeting,
}: {
  capabilities: SalesMeetingCapabilities;
  meeting: SalesMeeting;
}): React.ReactElement => {
  const toggle = useToggleSmsReminder();

  const templatesQuery = useQuery({
    queryKey: ENABLED_TEMPLATES_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sales_sms_template")
        .select("*")
        .eq("enabled", true)
        .order("type", { ascending: true })
        .order("sortOrder", { ascending: true })
        .order("name", { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as SalesSmsTemplate[];
    },
  });

  const templates = templatesQuery.data ?? [];

  const items = templates.map((template) => ({
    key: template.id,
    label: `${SALES_SMS_TEMPLATE_TYPE_LABEL[template.type]} · ${template.name}`,
    onClick: () => {
      const body = renderSmsTemplateBody(meeting, template.body);
      void copyTextToClipboard(body);
      void message.success("Skopiowano treść SMS");
      if (capabilities.canUpdate && !meeting.smsReminders?.[template.type]) {
        toggle.mutate({ meeting, kind: template.type, active: true });
      }
    },
  }));

  return (
    <Dropdown menu={{ items }} trigger={["click"]} disabled={!items.length}>
      <Button icon={<CopyOutlined />} loading={templatesQuery.isLoading}>
        Kopiuj SMS
      </Button>
    </Dropdown>
  );
};
